<script lang="ts">
  import { onMount } from 'svelte';
  import type { DevicePoolSummary, IngressConnectionSummary } from '@ucc/shared';
  import { ApiError, createDevicePool, deleteDevicePool, fetchDevicePools, fetchIngressConnections } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  let pools = $state<DevicePoolSummary[]>([]);
  let connections = $state<IngressConnectionSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let formError = $state<string | null>(null);
  let saving = $state(false);

  let newName = $state('');
  let newDescription = $state('');
  let newConnectionId = $state('');

  const role = $derived(session.user?.role);
  const isStaff = $derived(role === 'admin' || role === 'user');
  const connectionById = (id: string | null | undefined) => connections.find((c) => c.id === id) ?? null;

  onMount(() => {
    void reload();
  });

  async function reload() {
    loading = true;
    error = null;
    try {
      const [p, c] = await Promise.all([fetchDevicePools(), fetchIngressConnections()]);
      pools = p.pools;
      connections = c.connections;
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load Device Pools';
      pools = [];
      connections = [];
    } finally {
      loading = false;
    }
  }

  async function onCreate(e: Event) {
    e.preventDefault();
    formError = null;
    const name = newName.trim();
    if (!name) {
      formError = 'Name is required.';
      return;
    }
    saving = true;
    try {
      await createDevicePool({
        name,
        description: newDescription.trim() || null,
        connectionId: newConnectionId || null,
      });
      newName = '';
      newDescription = '';
      newConnectionId = '';
      await reload();
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Create failed';
    } finally {
      saving = false;
    }
  }

  async function onDelete(id: string) {
    formError = null;
    try {
      await deleteDevicePool(id);
      await reload();
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Delete failed';
    }
  }
</script>

<div class="page">
  <h2>Device Pool</h2>
  <p class="sub">
    Staff-only. Controls how a device's raw payload gets <em>into</em> UCC. Not related to Groups
    (client upstream delivery) — no shared data, no shared page, on purpose. Click a pool to edit
    it or manage its devices.
  </p>

  {#if !isStaff}
    <Card title="Staff only">
      <p class="muted">Only admin/staff can manage Device Pools.</p>
    </Card>
  {:else}
    <Card title="Create Device Pool" subtitle="Pick a pre-created connection (see Settings → Ingress Connection Methods)">
      <form class="form" onsubmit={(e) => void onCreate(e)}>
        <label class="field">
          <span class="label">Name</span>
          <input class="input" type="text" maxlength={120} bind:value={newName} disabled={saving} required />
        </label>
        <label class="field">
          <span class="label">Description (optional)</span>
          <input class="input" type="text" bind:value={newDescription} disabled={saving} />
        </label>
        <label class="field">
          <span class="label">Connection</span>
          <select class="input" bind:value={newConnectionId} disabled={saving}>
            <option value="">(none yet)</option>
            {#each connections as c (c.id)}
              <option value={c.id}>{c.name} ({c.type})</option>
            {/each}
          </select>
        </label>
        {#if connections.length === 0}
          <p class="muted small">
            No Ingress Connections exist yet — create one under
            <a href="#/settings/ingress-connections">Settings → Ingress Connection Methods</a> first.
          </p>
        {/if}
        {#if formError}
          <p class="err" role="alert">{formError}</p>
        {/if}
        <div class="actions">
          <Button type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create pool'}</Button>
        </div>
      </form>
    </Card>

    <Card title="Pools" subtitle={loading ? 'Loading…' : `${pools.length} pool(s)`}>
      {#if error}
        <p class="err" role="alert">{error}</p>
      {:else if loading}
        <p class="muted">Loading…</p>
      {:else if pools.length === 0}
        <p class="muted">No Device Pools yet.</p>
      {:else}
        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Connection</th>
                <th>Devices</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each pools as p (p.id)}
                {@const conn = connectionById(p.connectionId)}
                <tr>
                  <td><a class="row-link" href="#/device-pools/{p.id}">{p.name}</a>{#if p.description}<div class="muted small">{p.description}</div>{/if}</td>
                  <td class="mono small">{conn ? `${conn.name} (${conn.type})` : '—'}</td>
                  <td>{p.deviceCount ?? 0}</td>
                  <td class="row-actions">
                    <button
                      type="button"
                      class="linkish"
                      disabled={(p.deviceCount ?? 0) > 0}
                      title={(p.deviceCount ?? 0) > 0 ? 'Remove every device first' : ''}
                      onclick={() => void onDelete(p.id)}>Delete</button
                    >
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </Card>
  {/if}
</div>

<style>
  .page {
    max-width: 56rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .sub {
    margin: 0.35rem 0 0.5rem;
    color: var(--text-muted);
    font-size: 0.95rem;
    line-height: 1.45;
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .label {
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  .input {
    font: inherit;
    padding: 0.5rem 0.65rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border, #2a2a32);
    background: var(--surface-2, #121218);
    color: var(--text);
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .err {
    color: #f87171;
    margin: 0;
    font-size: 0.9rem;
  }
  .muted {
    color: var(--text-muted);
  }
  .small {
    font-size: 0.8rem;
  }
  .table-wrap {
    overflow-x: auto;
  }
  .table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    text-align: left;
    padding: 0.55rem 0.4rem;
    border-bottom: 1px solid var(--border, #2a2a32);
    vertical-align: top;
  }
  th {
    color: var(--text-muted);
    font-weight: 500;
    font-size: 0.8rem;
  }
  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }
  .row-actions {
    white-space: nowrap;
    text-align: right;
  }
  .row-link {
    color: var(--text);
    text-decoration: none;
    font-weight: 500;
  }
  .row-link:hover {
    color: var(--accent, #3b82f6);
  }
  .linkish {
    background: none;
    border: none;
    color: var(--accent, #3b82f6);
    cursor: pointer;
    font: inherit;
    padding: 0.15rem 0.35rem;
  }
  .linkish:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
